import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DejaVuSansBoldTtf from 'dejavu-fonts-ttf/ttf/DejaVuSans-Bold.ttf';
import DejaVuSansTtf from 'dejavu-fonts-ttf/ttf/DejaVuSans.ttf';
import dayjs from 'dayjs';
import { formatBookingCurrency } from './formatBookingCurrency';
import type { AssignedExpenseInvoice } from '../../Shared/Types/bookingTypes';

export interface BookingExpenseInvoicesPdfParams {
  expenseInvoices: AssignedExpenseInvoice[];
  offerName?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface JsPdfWithAutoTable extends jsPDF {
  lastAutoTable?: { finalY: number };
}

const PDF_FONT_FAMILY = 'DejaVuSans';
const PDF_MARGIN_LEFT = 10;
const PDF_MARGIN_RIGHT = 10;

let cachedRegularFontBinary: string | null = null;
let cachedBoldFontBinary: string | null = null;

const arrayBufferToBinaryString = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    let s = '';
    for (let j = 0; j < chunk.length; j++) s += String.fromCharCode(chunk[j]);
    binary += s;
  }
  return binary;
};

const ensureFontsRegistered = async (doc: jsPDF): Promise<void> => {
  if (!cachedRegularFontBinary) {
    const res = await fetch(DejaVuSansTtf);
    cachedRegularFontBinary = arrayBufferToBinaryString(await res.arrayBuffer());
  }
  if (!cachedBoldFontBinary) {
    const res = await fetch(DejaVuSansBoldTtf);
    cachedBoldFontBinary = arrayBufferToBinaryString(await res.arrayBuffer());
  }
  doc.addFileToVFS('DejaVuSans.ttf', cachedRegularFontBinary);
  doc.addFont('DejaVuSans.ttf', PDF_FONT_FAMILY, 'normal');
  doc.addFileToVFS('DejaVuSans-Bold.ttf', cachedBoldFontBinary);
  doc.addFont('DejaVuSans-Bold.ttf', PDF_FONT_FAMILY, 'bold');
};

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '—';
};

const resolveDocumentRef = (inv: AssignedExpenseInvoice): string => {
  const ref = inv.documentReference?.trim();
  if (ref) return ref;
  const num = inv.invoiceNumber?.trim();
  if (num) return num;
  return `#${inv.expenseInvoiceId}`;
};

const resolveIssuerName = (inv: AssignedExpenseInvoice): string =>
  inv.displayIssuerName?.trim() || inv.sellerName?.trim() || 'Koszt uproszczony';

const resolveDescriptionAndTags = (inv: AssignedExpenseInvoice): string => {
  const parts: string[] = [];
  const desc = inv.description?.trim();
  if (desc) parts.push(desc);
  const tagNames = (inv.tags ?? [])
    .map((t) => t.name?.trim())
    .filter((n): n is string => Boolean(n));
  if (tagNames.length > 0) parts.push(tagNames.join(', '));
  return parts.join(' | ') || '—';
};

const resolveAssignmentTagName = (inv: AssignedExpenseInvoice): string =>
  inv.assignmentTagName?.trim() || '—';

const resolvePaymentMethod = (inv: AssignedExpenseInvoice): string => {
  const val = inv.paymentMethod;
  if (val === 'cash') return 'Gotówka';
  if (val === 'transfer') return 'Przelew';
  return '—';
};

const resolveAllocationPercent = (inv: AssignedExpenseInvoice): string => {
  if (!inv.totalGross || inv.totalGross === 0) return '0,0%';
  return `${((inv.allocatedAmount / inv.totalGross) * 100).toFixed(1).replace('.', ',')}%`;
};

export const createBookingExpenseInvoicesPdfBlob = async (
  params: BookingExpenseInvoicesPdfParams
): Promise<Blob> => {
  const { expenseInvoices, offerName, dateFrom, dateTo } = params;

  const doc = new jsPDF({ orientation: 'portrait', format: 'a4' }) as JsPdfWithAutoTable;
  await ensureFontsRegistered(doc);
  doc.setFont(PDF_FONT_FAMILY, 'normal');

  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PDF_MARGIN_LEFT - PDF_MARGIN_RIGHT;

  // Title
  const title = offerName?.trim()
    ? `Dokumenty kosztowe — ${offerName.trim()}`
    : 'Dokumenty kosztowe';
  const titleLines = doc.splitTextToSize(title, contentWidth);
  doc.setFontSize(13);
  doc.setFont(PDF_FONT_FAMILY, 'bold');
  doc.text(titleLines, PDF_MARGIN_LEFT, 16);

  // Date range
  const termY = 16 + titleLines.length * 6 + 2;
  doc.setFontSize(10);
  doc.setFont(PDF_FONT_FAMILY, 'normal');
  doc.text(`Termin: ${formatDate(dateFrom)} — ${formatDate(dateTo)}`, PDF_MARGIN_LEFT, termY);

  // Table
  const tableStartY = termY + 7;
  const totalAllocated = expenseInvoices.reduce((sum, inv) => sum + inv.allocatedAmount, 0);

  autoTable(doc, {
    startY: tableStartY,
    margin: { left: PDF_MARGIN_LEFT, right: PDF_MARGIN_RIGHT },
    head: [[
      'Dokument',
      'Wystawca',
      'Opis / tagi',
      'Rodzaj wydatku',
      'Notatka',
      'Data wystawienia',
      'Wartość brutto',
      'Kwota przypisana',
      '% alokacji',
    ]],
    body: expenseInvoices.map((inv) => [
      resolveDocumentRef(inv),
      resolveIssuerName(inv),
      resolveDescriptionAndTags(inv),
      resolveAssignmentTagName(inv),
      resolvePaymentMethod(inv),
      formatDate(inv.issueDate),
      formatBookingCurrency(inv.totalGross),
      formatBookingCurrency(inv.allocatedAmount),
      resolveAllocationPercent(inv),
    ]),
    styles: {
      fontSize: 7.5,
      cellPadding: 2,
      font: PDF_FONT_FAMILY,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fontStyle: 'bold',
      font: PDF_FONT_FAMILY,
      fillColor: [33, 150, 243],
      textColor: 255,
      fontSize: 8,
    },
    alternateRowStyles: {
      fillColor: [245, 248, 255],
    },
    columnStyles: {
      5: { halign: 'center' },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' },
    },
  });

  // Summary row
  const finalY = (doc.lastAutoTable?.finalY ?? tableStartY) + 8;
  const pageHeight = doc.internal.pageSize.getHeight();
  const summaryY = finalY > pageHeight - 16 ? (doc.addPage(), 18) : finalY;

  doc.setFontSize(10);
  doc.setFont(PDF_FONT_FAMILY, 'bold');
  doc.text(
    `Razem przypisano: ${formatBookingCurrency(totalAllocated)}`,
    pageWidth - PDF_MARGIN_RIGHT,
    summaryY,
    { align: 'right' }
  );

  return doc.output('blob');
};

export const downloadBookingExpenseInvoicesPdf = (blob: Blob, fileName: string): void => {
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error('Wygenerowany PDF jest pusty.');
  }
  const url = window.URL.createObjectURL(blob);
  try {
    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(url);
  }
};
